import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useColors } from "../constants/Colors";
import { useAuth } from "../contexts/AuthContext";
import { useSidebar } from "../contexts/SidebarContext";
import { trpcReact } from "../trpc_client";
import ImageUploader, { UploadedImage } from "../components/ImageUploader";
import { getFa5Icon } from "../constants/Hobbies";
import { usePostHog } from "posthog-js/react";

const USERNAME_MIN_LENGTH = 2;
const USERNAME_MAX_LENGTH = 30;
const PROFILE_TEXT_MAX_LENGTH = 500;

const hobbiesData = [
  { id: "0", icon: "🌈", name: "LQBTQ+ Friendly", library: "EMOJIE" },
  { id: "1", icon: "book", name: "Reading", library: "FontAwesome5" },
  { id: "2", icon: "gamepad", name: "Gaming", library: "FontAwesome5" },
  { id: "3", icon: "utensils", name: "Cooking", library: "FontAwesome5" },
  { id: "4", icon: "plane", name: "Travel", library: "FontAwesome5" },
  { id: "5", icon: "camera", name: "Photography", library: "FontAwesome5" },
  { id: "6", icon: "music", name: "Music", library: "FontAwesome5" },
  { id: "7", icon: "pencil-alt", name: "Writing", library: "FontAwesome5" },
  { id: "8", icon: "running", name: "Running", library: "FontAwesome5" },
  { id: "9", icon: "bicycle", name: "Cycling", library: "FontAwesome5" },
  { id: "10", icon: "hiking", name: "Hiking", library: "FontAwesome5" },
  { id: "11", icon: "swimmer", name: "Swimming", library: "FontAwesome5" },
  { id: "12", icon: "palette", name: "Art", library: "FontAwesome5" },
  { id: "13", icon: "seedling", name: "Gardening", library: "FontAwesome5" },
  { id: "14", icon: "om", name: "Yoga", library: "FontAwesome5" },
  { id: "15", icon: "spa", name: "Wellness", library: "FontAwesome5" },
  { id: "16", icon: "theater-masks", name: "Theater", library: "FontAwesome5" },
  { id: "17", icon: "chess", name: "Chess", library: "FontAwesome5" },
  {
    id: "18",
    icon: "hands-helping",
    name: "Volunteering",
    library: "FontAwesome5",
  },
  { id: "19", icon: "dumbbell", name: "Fitness", library: "FontAwesome5" },
  { id: "20", icon: "dog", name: "Dogs", library: "FontAwesome5" },
  { id: "21", icon: "film", name: "Movies", library: "FontAwesome5" },
  { id: "22", icon: "tv", name: "TV", library: "FontAwesome5" },
  { id: "23", icon: "laptop-code", name: "Coding", library: "FontAwesome5" },
  { id: "24", icon: "microphone", name: "Singing", library: "FontAwesome5" },
  { id: "25", icon: "guitar", name: "Guitar", library: "FontAwesome5" },
  {
    id: "26",
    icon: "basketball-ball",
    name: "Basketball",
    library: "FontAwesome5",
  },
  { id: "27", icon: "wine-glass", name: "Wine", library: "FontAwesome5" },
  { id: "28", icon: "globe-europe", name: "Travel", library: "FontAwesome5" },
  { id: "29", icon: "paint-brush", name: "Painting", library: "FontAwesome5" },
  { id: "30", icon: "coffee", name: "Coffee", library: "FontAwesome5" },
  { id: "31", icon: "bread-slice", name: "Baking", library: "FontAwesome5" },
  { id: "32", icon: "campground", name: "Camping", library: "FontAwesome5" },
  { id: "33", icon: "mountain", name: "Mountains", library: "FontAwesome5" },
  { id: "34", icon: "fist-raised", name: "Activism", library: "FontAwesome5" },
  {
    id: "35",
    icon: "hourglass-half",
    name: "Meditation",
    library: "FontAwesome5",
  },
  { id: "36", icon: "star", name: "Astronomy", library: "FontAwesome5" },
  { id: "37", icon: "tshirt", name: "Fashion", library: "FontAwesome5" },
  { id: "38", icon: "pen-fancy", name: "Journaling", library: "FontAwesome5" },
  { id: "39", icon: "leaf", name: "Veganism", library: "FontAwesome5" },
  { id: "40", icon: "atom", name: "Science", library: "FontAwesome5" },
  { id: "41", icon: "bullhorn", name: "Podcasts", library: "FontAwesome5" },
  { id: "42", icon: "brain", name: "Psychology", library: "FontAwesome5" },
];

const renderIcon = (icon: string, library: string, textColor: string) => {
  if (library === "EMOJIE") {
    return <span className="text-sm mr-2">{icon}</span>;
  }
  const IconComponent = getFa5Icon(icon);
  if (IconComponent) {
    return (
      <IconComponent size={14} style={{ color: textColor, marginRight: 8 }} />
    );
  }
  return null;
};

const extractProfileObjectNameFromUrl = (url: string): string | null => {
  try {
    const parsed = new URL(url);
    const segments = decodeURIComponent(parsed.pathname)
      .split("/")
      .filter(Boolean);
    const profileIndex = segments.findIndex((segment) => segment === "profile");
    if (profileIndex >= 0 && profileIndex + 1 < segments.length) {
      return segments.slice(profileIndex + 1).join("/");
    }
    return segments.length ? segments[segments.length - 1] : null;
  } catch {
    const cleanUrl = url.split("?")[0];
    const segments = cleanUrl.split("/").filter(Boolean);
    const profileIndex = segments.findIndex((segment) => segment === "profile");
    if (profileIndex >= 0 && profileIndex + 1 < segments.length) {
      return segments.slice(profileIndex + 1).join("/");
    }
    return segments.length ? segments[segments.length - 1] : null;
  }
};

const EditProfile: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const posthog = usePostHog();
  const { isCollapsed } = useSidebar();
  const colors = useColors();

  const [username, setUsername] = useState("");
  const [profileText, setProfileText] = useState("");
  const [instagramHandle, setInstagramHandle] = useState("");
  const [selectedHobbies, setSelectedHobbies] = useState<string[]>([]);
  const [images, setImages] = useState<UploadedImage[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  const { data: currentUser, isLoading } =
    trpcReact.getCurrentUserProfile.useQuery(undefined, { enabled: !!user });

  const updateUserMutation = trpcReact.updateUser.useMutation();
  const updateProfileTextMutation =
    trpcReact.addProfileTextAndInterest.useMutation();
  const updateImagesMutation = trpcReact.updateProfileImages.useMutation();

  useEffect(() => {
    if (currentUser) {
      setUsername(currentUser.username || "");
      setProfileText(currentUser.profile_text || "");
      setInstagramHandle(currentUser.instagram_handle || "");
      setSelectedHobbies(currentUser.profile_interests || []);

      if (currentUser.profile_images && currentUser.profile_images.length > 0) {
        const existingImages: UploadedImage[] = currentUser.profile_images.map(
          (url: string, index: number) => ({
            uri: url,
            object_name: `existing:${
              extractProfileObjectNameFromUrl(url) || `unknown_${index}`
            }`,
            get_url: url,
            progress: 1,
          }),
        );
        setImages(existingImages);
      }
    }
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) return;

    const hasUsernameChanged = username !== (currentUser.username || "");
    const hasProfileTextChanged =
      profileText !== (currentUser.profile_text || "");
    const hasInstagramChanged =
      instagramHandle !== (currentUser.instagram_handle || "");
    const hasHobbiesChanged =
      JSON.stringify(selectedHobbies) !==
      JSON.stringify(currentUser.profile_interests || []);
    const hasImagesChanged =
      images.length !== (currentUser.profile_images?.length || 0) ||
      images.some(
        (img, idx) => img.get_url !== currentUser.profile_images?.[idx],
      );

    setHasChanges(
      hasUsernameChanged ||
        hasProfileTextChanged ||
        hasInstagramChanged ||
        hasHobbiesChanged ||
        hasImagesChanged,
    );
  }, [
    username,
    profileText,
    instagramHandle,
    selectedHobbies,
    images,
    currentUser,
  ]);

  const handleSave = async () => {
    if (!currentUser?.user_id) return;

    if (
      username.trim().length < USERNAME_MIN_LENGTH ||
      username.trim().length > USERNAME_MAX_LENGTH
    ) {
      posthog?.capture("profile_update_failed", {
        code: "invalid_username",
        message: "Username length validation failed",
      });
      setSaveError(
        `Username must be between ${USERNAME_MIN_LENGTH} and ${USERNAME_MAX_LENGTH} characters`,
      );
      return;
    }

    if (images.some((img) => img.progress < 1)) {
      posthog?.capture("profile_update_failed", {
        code: "upload_in_progress",
        message: "Profile image uploads are still in progress",
      });
      setSaveError("Please wait for all images to finish uploading");
      return;
    }

    if (images.length < 1) {
      posthog?.capture("profile_update_failed", {
        code: "missing_images",
        message: "At least one profile image is required",
      });
      setSaveError("You need at least 1 image");
      return;
    }

    posthog?.capture("profile_update_started");
    setIsSaving(true);
    setSaveError(null);

    try {
      if (
        username !== currentUser.username ||
        instagramHandle !== (currentUser.instagram_handle || "")
      ) {
        await updateUserMutation.mutateAsync({
          user_id: currentUser.user_id,
          username: username.trim(),
          instagram_handle: instagramHandle.trim() || undefined,
        });
      }

      if (
        profileText !== (currentUser.profile_text || "") ||
        JSON.stringify(selectedHobbies) !==
          JSON.stringify(currentUser.profile_interests || [])
      ) {
        await updateProfileTextMutation.mutateAsync({
          user_id: currentUser.user_id,
          profile_text: profileText.trim(),
          profile_interests: selectedHobbies,
        });
      }

      const orderedImageNames = images
        .map((img) => {
          if (img.object_name.startsWith("existing:")) {
            return img.object_name.replace(/^existing:/, "").trim();
          }
          const uploadedName = img.object_name.replace(/^profile\//, "").trim();
          if (uploadedName) return uploadedName;
          return extractProfileObjectNameFromUrl(img.get_url || img.uri) || "";
        })
        .filter((name) => name !== "");

      if (orderedImageNames.length > 0) {
        await updateImagesMutation.mutateAsync({
          data: orderedImageNames,
        });
      }

      posthog?.capture("profile_update_success");
      navigate("/profile");
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to save profile. Please try again.";
      console.error("Error saving profile:", error);
      posthog?.capture("profile_update_failed", {
        code: "profile_update_failed",
        message,
      });
      setSaveError(
        message || "Failed to save profile. Please try again.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      if (
        window.confirm(
          "You have unsaved changes. Are you sure you want to leave?",
        )
      ) {
        navigate("/profile");
      }
    } else {
      navigate("/profile");
    }
  };

  const toggleHobby = (hobbyId: string) => {
    setSelectedHobbies((prev) =>
      prev.includes(hobbyId)
        ? prev.filter((id) => id !== hobbyId)
        : [...prev, hobbyId],
    );
  };

  const handleRemoveImage = (index: number) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const sectionCard: React.CSSProperties = {
    backgroundColor: colors.containerNeutralSolidSurface,
    borderRadius: 20,
    boxShadow:
      "0 2px 12px rgba(2, 2, 16, 0.04), 0 0 0 1px rgba(2, 2, 16, 0.03)",
  };

  if (!user) {
    navigate("/profile");
    return null;
  }

  if (isLoading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: colors.containerNeutralSolidSurface }}
      >
        <div className="text-center">
          <div
            className="w-12 h-12 border-4 rounded-full animate-spin mx-auto mb-4"
            style={{
              borderColor: colors.containerNeutralLightMedium,
              borderTopColor: colors.accentColor,
            }}
          />
          <p
            className="font-gnarly-regular"
            style={{ color: colors.textNeutralMuted }}
          >
            {t("loading") || "Loading..."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen font-gnarly-regular"
      style={{ backgroundColor: colors.containerNeutralSolidSurface }}
    >
      {/* Fixed Header */}
      <header
        className={`fixed top-0 right-0 z-40 transition-all duration-300 ease-in-out left-0 ${
          isCollapsed ? "lg:left-16" : "lg:left-56"
        }`}
        style={{
          backgroundColor: "rgba(255, 255, 255, 0.85)",
          backdropFilter: "blur(16px)",
          WebkitBackdropFilter: "blur(16px)",
          boxShadow: "none",
        }}
      >
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-3.5 flex items-center justify-between">
          <button
            onClick={handleCancel}
            className="w-10 h-10 -ml-2 rounded-full flex items-center justify-center hover:scale-105 active:scale-95 transition-all duration-150"
            style={{ color: colors.textNeutralDefault }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M19 12H5" />
              <path d="m12 19-7-7 7-7" />
            </svg>
          </button>

          <h1
            className="text-lg font-gnarly-semibold tracking-tight"
            style={{ color: colors.textNeutralDefault }}
          >
            {t("profile_tab.edit_profile.title") || "Edit Profile"}
          </h1>

          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-5 py-2.5 rounded-full font-gnarly-semibold text-sm transition-all duration-150 hover:scale-[1.03] active:scale-[0.97] disabled:opacity-40"
            style={{
              backgroundColor: colors.accentColor,
              color: colors.textNeutralOnPrimary,
            }}
          >
            {isSaving ? t("saving") || "Saving..." : t("save") || "Save"}
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-2xl mx-auto px-4 sm:px-6 pt-24 pb-12 space-y-5">
        {saveError && (
          <div
            className="px-5 py-4 rounded-2xl text-sm font-gnarly-regular"
            style={{
              backgroundColor: "rgba(220, 54, 46, 0.07)",
              color: "#B02B25",
            }}
          >
            {saveError}
          </div>
        )}

        {/* ─── Username ───────────────────────────────────────── */}
        <section className="p-5" style={sectionCard}>
          <label
            className="block text-xs font-gnarly-semibold uppercase tracking-wider mb-3"
            style={{ color: colors.textNeutralMuted }}
          >
            {t("profile_tab.edit_profile.username_placeholder") || "Username"}
          </label>
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            maxLength={USERNAME_MAX_LENGTH}
            className="cf-input-boxed text-[15px]"
            style={{ color: colors.textNeutralDefault }}
            placeholder="Your name"
          />
          <p
            className="mt-2 text-[11px]"
            style={{ color: colors.textNeutralMuted }}
          >
            {username.length}/{USERNAME_MAX_LENGTH}
          </p>
        </section>

        {/* ─── Instagram Handle ───────────────────────────────── */}
        <section className="p-5" style={sectionCard}>
          <label
            className="block text-xs font-gnarly-semibold uppercase tracking-wider mb-3"
            style={{ color: colors.textNeutralMuted }}
          >
            Instagram
          </label>
          <div className="relative">
            <span
              className="absolute left-[18px] top-1/2 -translate-y-1/2 text-[15px]"
              style={{ color: colors.textNeutralMuted }}
            >
              @
            </span>
            <input
              type="text"
              value={instagramHandle}
              onChange={(e) =>
                setInstagramHandle(e.target.value.replace(/^@/, ""))
              }
              maxLength={30}
              className="cf-input-boxed text-[15px] !pl-8"
              style={{ color: colors.textNeutralDefault }}
              placeholder="instagram_handle"
            />
          </div>
        </section>

        {/* ─── About Me ───────────────────────────────────────── */}
        <section className="p-5" style={sectionCard}>
          <label
            className="block text-xs font-gnarly-semibold uppercase tracking-wider mb-3"
            style={{ color: colors.textNeutralMuted }}
          >
            {t("profile_tab.edit_profile.profile_text_placeholder") ||
              "About me"}
          </label>
          <textarea
            value={profileText}
            onChange={(e) => setProfileText(e.target.value)}
            maxLength={PROFILE_TEXT_MAX_LENGTH}
            rows={5}
            className="cf-textarea text-[15px]"
            style={{ color: colors.textNeutralDefault }}
            placeholder="Tell others about yourself..."
          />
          <p
            className="mt-2 text-[11px]"
            style={{ color: colors.textNeutralMuted }}
          >
            {profileText.length}/{PROFILE_TEXT_MAX_LENGTH}
          </p>
        </section>

        {/* ─── Photos ─────────────────────────────────────────── */}
        <section className="p-5" style={sectionCard}>
          <label
            className="block text-xs font-gnarly-semibold uppercase tracking-wider mb-3"
            style={{ color: colors.textNeutralMuted }}
          >
            {t("signup.images.title") || "Photos"}
          </label>
          <ImageUploader
            images={images}
            onImagesChange={setImages}
            onRemoveImage={handleRemoveImage}
            kind="profile"
            minImages={1}
            maxImages={9}
            showMainBadge
          />
        </section>

        {/* ─── Interests ──────────────────────────────────────── */}
        <section className="p-5" style={sectionCard}>
          <label
            className="block text-xs font-gnarly-semibold uppercase tracking-wider mb-3"
            style={{ color: colors.textNeutralMuted }}
          >
            {t("profile_tab.hobbies") || "Interests"}
          </label>
          <div className="flex flex-wrap gap-2">
            {hobbiesData.map((hobby) => {
              const isSelected = selectedHobbies.includes(hobby.id);
              return (
                <button
                  key={hobby.id}
                  onClick={() => toggleHobby(hobby.id)}
                  className="px-4 py-2.5 rounded-full flex items-center text-sm font-gnarly-semibold transition-all duration-150 hover:scale-[1.02] active:scale-[0.97]"
                  style={{
                    backgroundColor: isSelected
                      ? `${colors.accentColor}14`
                      : "rgba(2, 2, 16, 0.04)",
                    color: isSelected
                      ? colors.accentColor
                      : colors.textNeutralDefault,
                    border: `1.5px solid ${isSelected ? colors.accentColor : "transparent"}`,
                  }}
                >
                  {renderIcon(
                    hobby.icon,
                    hobby.library,
                    isSelected ? colors.accentColor : colors.textNeutralDefault,
                  )}
                  {hobby.name}
                </button>
              );
            })}
          </div>
        </section>
      </main>
    </div>
  );
};

export default EditProfile;
